// Typen der editor-backend-Schnittstelle (REST /api/v1 + WS /ws/v1).
// Feld- und Typnamen sind deckungsgleich mit docs/api.md und den Pydantic-Modellen des
// Backends (apps/editor-backend). Sie sind hier zentral gehalten, damit Client, Views und
// Tests denselben Vertrag teilen.

// ── Laufzeit-/Frontend-Konfiguration (GET /config) ───────────────────────────────────

// Vom Backend gelieferte Frontend-Konfiguration. Steuert bislang nur die Trainings-Ansicht:
// ob die eingebettete Jupyter-Umgebung angeboten wird und unter welchem same-origin-Basispfad
// JupyterLab erreichbar ist (Default `/jupyter`). Das Frontend nutzt das fürs Graceful-Handling —
// ist Training deaktiviert oder der Server nicht gestartet, zeigt es einen Hinweis statt eines
// kaputten iframes.
export interface AppConfig {
  trainingEnabled: boolean;
  jupyterBasePath: string;
  // Der aktuell konfigurierte Trainings-/GPU-Pod (Issue #222), geheimnisfreie Kurzform für die
  // Anzeige in der Trainings-Ansicht. null, wenn (noch) nichts konfiguriert ist.
  trainingPod?: TrainingPodSummary | null;
  // Ob die Selbstregistrierung angeboten wird (BFF_ALLOW_SELF_REGISTRATION, Issue #191). Steuert,
  // ob der Anmelde-Screen das Registrieren-Formular zeigt. Optional gehalten, damit ein älteres
  // Backend ohne das Feld sauber als „aus" behandelt wird.
  allowSelfRegistration?: boolean;
}

// ── Trainings-/GPU-Pod-Konfiguration (Issue #222) ────────────────────────────────────

// Geheimnisfreie Kurzform des Trainings-/GPU-Pods für die UI: der gewählte Stand (Provider/Konto/
// GPU/Region), plus ein reines Ja/Nein, ob Provider-Zugangsdaten (als OpenBao-Handles) hinterlegt
// sind — nie die Handles selbst.
export interface TrainingPodSummary {
  // Nutzer-Kennung, zu der der angezeigte Pod gehört (Issue #314). null/fehlend, solange der
  // Nutzer nur die übernommene Instanz-Vorgabe sieht.
  owner?: string | null;
  enabled: boolean;
  provider: string;
  account?: string | null;
  gpuType?: string | null;
  gpuCount: number;
  region?: string | null;
  cloudType?: string | null;
  hasSecrets: boolean;
}

// Ressourcen-Obergrenzen des Pods (alle Felder optional).
export interface TrainingPodResources {
  cpuCores?: number | null;
  memoryGb?: number | null;
  maxRuntimeMin?: number | null;
  maxCostEur?: number | null;
}

// Volle Trainings-/GPU-Pod-Konfiguration des angemeldeten Nutzers (GET /training/pod).
// secretRefs trägt OpenBao-Handles ('bao:<pfad>#<feld>'), nie Klartext-Geheimnisse.
export interface TrainingPodConfig {
  schema_version: number;
  // Besitzer der Konfiguration (Issue #314 — je Nutzer, nicht instanzweit).
  owner?: string | null;
  enabled: boolean;
  provider: string;
  account?: string | null;
  gpuType?: string | null;
  gpuCount: number;
  region?: string | null;
  cloudType?: string | null;
  resources?: TrainingPodResources | null;
  secretRefs: Record<string, string>;
}

// Erlaubte Werte je Provider (GET /training/pod/options, Issue #368). Der Katalog ist
// Anbieterwissen und steht im Backend — RunPod hat andere GPU-Typen und Regionen als AWS, und
// 'local' (der eingebettete CPU-Pfad) hat weder das eine noch das andere.
export interface PodProviderOptions {
  gpuTypes: string[];
  regions: { value: string; label: string }[];
  cloudTypes: string[];
}

export interface PodOptionsResponse {
  providers: Record<string, PodProviderOptions>;
}

// Partielles Update der Pod-Konfiguration (PUT /training/pod): nur gesetzte Felder werden geändert.
export interface TrainingPodUpdate {
  enabled?: boolean;
  provider?: string;
  account?: string | null;
  gpuType?: string | null;
  gpuCount?: number;
  region?: string | null;
  cloudType?: string | null;
  resources?: TrainingPodResources | null;
  secretRefs?: Record<string, string> | null;
}

// ── Palette: Blocktypen (boris2py) ───────────────────────────────────────────────────

export interface BlockFlags {
  stateful: boolean;
  source: boolean;
  sink: boolean;
}

// Ein Blocktyp aus GET /palette/blocks. `inputs`/`outputs` sind 1-indizierte Portnummern
// (leeres Array = kein Port, null = variable Portzahl).
export interface BlockType {
  id: string;
  type: string;
  category: string;
  description: string;
  inputs: number[] | null;
  outputs: number[] | null;
  flags: BlockFlags;
}

export interface BlocksResponse {
  source: string;
  blocks: BlockType[];
}

// ── Palette: Symbole/Sensoren (plant-catalog) ────────────────────────────────────────

// Standard-Variable eines Sensors; `range` ist ein 2-Tupel [min, max] (Backend-Serialisierung).
export interface SymbolVariable {
  name: string;
  quantity: string;
  unit: string;
  range: [number, number] | null;
}

export interface SymbolPort {
  id: string;
  stream: string;
  direction: string;
}

// Ein Eintrag aus GET /palette/symbols — Behälter/Maschine ODER Sensor (dann mit `variables`).
export interface PaletteSymbol {
  id: string;
  label: string;
  label_en: string;
  category: string; // 'behaelter' | 'maschine' | 'sensor' | 'aktor_sollwert' | ...
  domains: string[];
  svg?: string;
  ports?: SymbolPort[];
  isa_tag?: string;
  variables?: SymbolVariable[];
  setpoint_bindings?: string[]; // an dieses Symbol bindbare Aktor-Sollwert-IDs (Regelmuster)
}

export interface SymbolsResponse {
  domain: string | null;
  symbols: PaletteSymbol[];
}

// ── Projekte ─────────────────────────────────────────────────────────────────────────

export interface ProjectSummary {
  id: string;
  name: string;
  domain: string | null;
  // Vier-Augen-Prinzip pro Projekt (Issue #114): ist es eingeschaltet, verlangt die
  // Deploy-Freigabe eine zweite Person. Default aus; optional für Rückwärtskompatibilität.
  fourEyes?: boolean;
}

export interface ProjectList {
  projects: ProjectSummary[];
}

export interface ProjectMeta {
  id: string;
  name: string;
  domain: string | null;
  defaultCycleMs: number;
  // Projektspezifischer OPC-UA-Endpoint (Issue #118), gegen den der Live-Lauf fährt (z. B.
  // 'opc.tcp://plc:4840'). null = kein projektspezifischer Endpoint gesetzt (dann greift beim
  // Live-Lauf die Env BFF_OPC_URL). Das Backend liefert das Feld immer, auch für Altprojekte.
  opcEndpoint?: string | null;
  rev: string | null;
  createdAt: string | null;
  // Gesetzt bei per BORIS-Import angelegten Projekten (Issue #27): { bsyFile }. Die Ausführung
  // läuft dann über das boris-gestützte Executable statt über den nativen Rechenkern.
  borisImport?: { bsyFile?: string } | null;
  // Vier-Augen-Prinzip pro Projekt (Issue #114): siehe ProjectSummary.fourEyes.
  fourEyes?: boolean;
}

// Antwort von PUT /projects/{id}/four-eyes (Issue #114): der neue Zustand des Schalters.
export interface ProjectFourEyesResult {
  id: string;
  fourEyes: boolean;
}

export interface ProjectCreate {
  id: string;
  name: string;
  domain?: string | null;
  defaultCycleMs?: number;
}

// Body von PUT /projects/{id}/settings (Issue #118): partielles Update von OPC-Endpoint und/oder
// Standard-Zykluszeit. Nur die tatsächlich gesetzten Felder werden geschrieben; opcEndpoint darf
// explizit auf null gesetzt werden, um den projektspezifischen Endpoint zu entfernen (defaultCycleMs
// < 1 quittiert das Backend mit 422).
export interface ProjectSettingsUpdate {
  opcEndpoint?: string | null;
  defaultCycleMs?: number;
}

// ── Signalkatalog (Vertrag, signal-catalog.schema.json) ──────────────────────────────

export interface Range {
  min: number;
  max: number;
}

export interface Limits {
  lolo?: number;
  lo?: number;
  hi?: number;
  hihi?: number;
}

export interface CatalogSource {
  symbol: string;
  variable: string;
}

export type OpcDatatype =
  | 'Boolean'
  | 'SByte'
  | 'Byte'
  | 'Int16'
  | 'UInt16'
  | 'Int32'
  | 'UInt32'
  | 'Int64'
  | 'UInt64'
  | 'Float'
  | 'Double'
  | 'String';

export type OpcAccess = 'read' | 'write' | 'read_write';

export type OpcQualityHandling = 'gate' | 'passthrough' | 'hold_last';

export interface OpcBinding {
  node_id: string;
  namespace?: string;
  datatype: OpcDatatype;
  access?: OpcAccess;
  quality_handling?: OpcQualityHandling;
}

export type Direction = 'input' | 'output';

// ── Modbus-Bindung (rein lesend, #156) ───────────────────────────────────────────────
export type ModbusKind = 'coil' | 'discrete_input' | 'holding' | 'input';

export type ModbusDatatype =
  | 'bool'
  | 'bool16'
  | 'int16'
  | 'uint16'
  | 'int32'
  | 'uint32'
  | 'int64'
  | 'uint64'
  | 'float32'
  | 'float64';

export type ModbusWordOrder = 'big' | 'little';

export interface ModbusScaling {
  factor: number;
  offset: number;
}

// Der modbus-Block einer Katalog-Variable (Register->Tag, read-only).
export interface ModbusBinding {
  kind: ModbusKind;
  address: number;
  datatype?: ModbusDatatype;
  word_order?: ModbusWordOrder;
  unit_id?: number;
  bit?: number;
  unit?: string;
  scaling?: ModbusScaling;
}

// Signalart (Issue #352) — WAS für eine Größe das ist, während `direction` nur die Richtung sagt.
// Fehlt das Feld, gilt die Vorgabe aus `direction`; Bestandskataloge bleiben damit gültig.
export type SignalKind = 'input' | 'output' | 'setpoint' | 'disturbance' | 'exogenous' | 'health';

export interface CatalogVariable {
  tag: string;
  name: string;
  /**
   * Lesbares deutsches Kurzlabel für die Anzeige (#373) — „NH4 Ablauf" statt „AT-201".
   * Rein optisch: der `tag` bleibt der Schlüssel und steht in der Oberfläche als Beleg daneben.
   * Fehlt das Label, zeigt die Oberfläche den `tag` — niemals eine leere Zeile.
   */
  label?: string;
  quantity: string;
  unit: string;
  range: Range;
  limits?: Limits;
  direction: Direction;
  kind?: SignalKind;
  source: CatalogSource;
  opc?: OpcBinding;
  modbus?: ModbusBinding;
  description?: string;
}

export interface SignalCatalog {
  schema_version: number;
  project: string;
  variables: CatalogVariable[];
}

// ── Anlagenschema (schematic.schema.json) ────────────────────────────────────────────
// Die visuelle P&ID-Darstellung der Anlagen-Ansicht: platzierte Prozess-Symbole mit ihren
// Canvas-Positionen und die Verrohrung (Ströme). Getrennt vom Signalkatalog gehalten — der
// Katalog wird weiterhin aus den Sensor-/Aktor-Variablen der platzierten Symbole erzeugt, das
// Schema trägt daneben die grafische Anordnung, damit die Canvas beim Projektwechsel wiederkommt.

export interface SchematicPosition {
  x: number;
  y: number;
}

// Ein platziertes Symbol im Schema. `id` ist zugleich die Herkunft (source.symbol) der aus dem
// Symbol erzeugten Katalog-Variablen — genau die Node-ID der Canvas-Instanz.
export interface SchematicNode {
  id: string;
  symbol: string; // Symbol-ID aus dem plant-catalog
  label?: string;
  position: SchematicPosition;
  geo?: SchematicGeo;
  params?: Record<string, unknown>;
}

// Eine Verrohrungskante zwischen zwei Schema-Knoten (gerichtet, from → to).
export interface SchematicEdge {
  id?: string;
  from: string; // Knoten-ID
  to: string; // Knoten-ID
  stream?: string; // Stromtyp-ID aus dem plant-catalog (wasser_abwasser | substrat_schlamm | …)
  geo?: SchematicEdgeGeo;
}

// ── Verortung (Issue #492) ───────────────────────────────────────────────────────────
// Neben der Canvas-Position trägt ein Knoten optional eine Welt-Position. Die Standortansicht
// (Issue #491) zeichnet daraus Körper auf das Luftbild; wer kein `geo` hat, erscheint dort nicht.

export type SchematicBody = 'zylinder' | 'quader' | 'marke';

// Ein Ring bzw. eine Trasse ist eine Folge von [lon, lat]-Paaren — Länge zuerst, wie in GeoJSON
// und CZML. Die umgekehrte Reihenfolge ist der häufigste Fehler beim Verorten.
export type LonLat = [number, number];

export interface SchematicGeo {
  lat: number;
  lon: number;
  hoehe_m?: number;
  koerper?: SchematicBody;
  groesse_m?: number;
  umriss?: LonLat[];
}

export interface SchematicEdgeGeo {
  trasse: LonLat[];
  illustrativ?: boolean;
}

export interface SiteCamera {
  hoehe_m?: number;
  neigung_grad?: number;
  richtung_grad?: number;
}

export interface SiteBackdrop {
  art: 'wms' | 'xyz' | 'keine';
  url?: string;
  layer?: string;
  attribution?: string;
}

export interface SchematicSite {
  name?: string;
  mittelpunkt: { lat: number; lon: number };
  kamera?: SiteCamera;
  kulisse?: SiteBackdrop;
}

export interface PlantSchematic {
  schema_version: number;
  project?: string;
  site?: SchematicSite;
  nodes: SchematicNode[];
  edges: SchematicEdge[];
}

// ── Signalgraph (graph.schema.json) ──────────────────────────────────────────────────

export interface GraphNodePosition {
  x: number;
  y: number;
}

// Bündelungs-Zuordnung eines Knotens (rein grafisch): id = stabiler Gruppen-Schlüssel (Herkunfts-
// Symbol), label = Anzeigename der Gruppe (optische Herkunft), function = funktionale Bezeichnung
// des Musters (logische Gruppierung). Von der Stamp-Funktion gesetzt; fehlt es, leitet das Frontend
// die Gruppe aus der Knoten-ID ab.
export interface GraphNodeGroup {
  id?: string;
  label?: string;
  function?: string;
}

export interface GraphNode {
  id: string;
  type: string;
  label?: string;
  params?: Record<string, unknown>;
  position?: GraphNodePosition;
  group?: GraphNodeGroup;
}

export interface PortRef {
  node: string;
  port: number;
}

export interface GraphEdge {
  id?: string;
  from: PortRef;
  to: PortRef;
}

export interface GraphMeta {
  project?: string;
  name?: string;
  cycle_time_s?: number;
  description?: string;
}

export interface SignalGraph {
  schema_version: number;
  nodes: GraphNode[];
  edges: GraphEdge[];
  meta?: GraphMeta;
}

// ── Validierung ──────────────────────────────────────────────────────────────────────

export interface ValidateFinding {
  severity: string; // 'error' | 'warning'
  code: string;
  message: string;
  tag?: string | null;
  target?: { kind?: string; id?: string; detail?: string } | null;
}

export interface ValidateResult {
  valid: boolean;
  findings: ValidateFinding[];
}

export interface WriteResult {
  rev: string;
}

// ── Modbus-Register-Map-Import + Mapping (#156) ──────────────────────────────────────────
// Ein aus einer Zeile der Register-Map abgeleiteter Bindungs-Vorschlag (niedrig-konfident).
// `binding` ist null, wenn die Zeile nicht bindbar war (z. B. kaputte Adresse). Der Client
// schickt die Vorschläge beim Bestätigen zurück (der Server ist zustandslos).
export interface ModbusProposalBinding {
  kind: ModbusKind;
  address: number;
  datatype: ModbusDatatype;
  word_order: ModbusWordOrder;
  scaling_factor: number;
  scaling_offset: number;
  unit: string | null;
  unit_id: number;
  bit: number | null;
}

export interface ModbusMapProposal {
  line: number;
  tag: string;
  confidence: number;
  issues: string[];
  description: string | null;
  binding: ModbusProposalBinding | null;
}

// Eine Zeile des Review-Overlays (daneben, nicht im Fragment).
export interface ModbusMapReviewRow {
  line: number;
  tag: string;
  valid: boolean;
  confidence: number;
  issues: string[];
  description: string | null;
  binding: ModbusBinding | null;
}

export interface ModbusMapReview {
  disclaimer: string;
  read_only: boolean;
  packets_sent: number;
  swept: boolean;
  rows: ModbusMapReviewRow[];
  needs_review: number[];
  open_points: string[];
}

export interface ModbusMapDraftResult {
  review: ModbusMapReview;
  proposals: ModbusMapProposal[];
}

// Optional erfasste schriftliche Freigabe fürs Onboarding-Audit (#160) — Tier-A wie Tier-B.
export interface OnboardingAuthorization {
  granted_by: string;
  granted_at: string;
  scope?: string;
}

// Eine menschliche Entscheidung zu genau einer Map-Zeile (bestätigen/verwerfen).
export interface ModbusMapDecision {
  line: number;
  confirmed: boolean;
  tag?: string;
  variable?: Record<string, unknown>;
  modbus?: Record<string, unknown>;
}

// ── UA-Browse-Onboarding (#152) ──────────────────────────────────────────────────────────
// Ein autorisiertes, read-only OPC-UA-Browse schlägt Tag-Kandidaten mit OPC-Bindung vor; der
// Betreiber kuratiert + bestätigt, bevor der Entwurf zum Signalkatalog wird.

// Eine Zeile im Review-Overlay je Browse-Kandidat (Hinweise NEBEN dem schema-konformen Katalog).
export interface UaBrowseReviewTag {
  tag: string;
  name: string;
  node_id: string;
  datatype: string;
  access: string;
  direction: string;
  unit: string;
  confidence: number;
}

export interface UaBrowseReview {
  disclaimer: string;
  read_only: boolean;
  endpoint: string;
  tags: UaBrowseReviewTag[];
  needs_review: string[];
  open_points: string[];
}

export interface UaBrowseDraftResult {
  catalog: SignalCatalog;
  review: UaBrowseReview;
}

// Eine menschliche Entscheidung zu genau einem Entwurfs-Tag (bestätigen/verwerfen, optional Korrektur).
export interface UaBrowseDecision {
  tag: string;
  confirmed: boolean;
  overrides?: Record<string, unknown>;
}

// Optional erfasste schriftliche Freigabe fürs Onboarding-Audit (#160).
export interface UaBrowseAuthorization {
  granted_by: string;
  granted_at: string;
  scope?: string;
}

// ── Default-Regelmuster stempeln (Issue #21) ─────────────────────────────────────────────

// Eine am Symbol platzierte Variable, für die ein Default-Muster gestempelt wird.
export interface StampVariable {
  tag: string;
  name: string;
  unit: string;
  direction: Direction;
  range?: { min: number; max: number } | [number, number];
  limits?: Limits;
  setpoint_id?: string; // ActuatorSetpoint-ID (kuratierte Regel-Defaults)
  pv_tag?: string; // zurückgeführter Prozesswert (Regelschleife)
  quality_tag?: string; // Qualitätssignal (Mess-Konditionierung)
}

export interface StampRequest {
  symbol_id: string;
  node_ref?: string; // Canvas-Instanz für stabile, idempotente Knoten-IDs
  variables: StampVariable[];
}

// Antwort des Stamp-Endpunkts: der gemergte Teilgraph plus was ergänzt/übersprungen wurde.
export interface StampResult {
  graph: SignalGraph;
  added: { nodes: GraphNode[]; edges: GraphEdge[] };
  skipped: string[];
  produced_tags: string[];
  valid: boolean;
  findings: ValidateFinding[];
}

// ── BORIS-Datei-Import (Issue #27) ─────────────────────────────────────────────────────
// Ein importiertes BORIS-System läuft über boris2py.generate_system (volle boris-Abdeckung); der
// abgeleitete Graph trägt die Darstellung, der abgeleitete Katalog die Ein-/Ausgänge. Offene
// Eingangsterminals werden zu Katalog-Eingängen (per CSV/OPC speisbar), interne Quellen bleiben —
// ein self-contained-System läuft sofort standalone.

// Ein Blocksteckbrief im Abdeckungsbericht (unterstützt oder Platzhalter).
export interface BorisCoverageBlock {
  index: number;
  type: string;
  name: string;
}

// Der Abdeckungsbericht: was gerechnet wird, was Platzhalter bleibt. `runnable` ist wahr, sobald
// kein nicht abgebildeter Block und kein ungelöster Superblock übrig ist (Rechenpfad vollständig).
export interface BorisCoverage {
  supported: BorisCoverageBlock[];
  unsupported: BorisCoverageBlock[];
  unresolved_superblocks: string[];
  runnable: boolean;
}

// Antwort des BORIS-Imports: abgeleiteter Graph + Katalog, der Bericht und die neue Revision. Graph
// und Katalog sind bereits ins Projekt geschrieben; das Projekt ist als BORIS-Import markiert.
export interface BorisImportResult {
  projectId: string;
  graph: SignalGraph;
  catalog: SignalCatalog;
  coverage: BorisCoverage;
  bsyFile: string;
  rev: string | null;
}

// ── Modelle / Registry ───────────────────────────────────────────────────────────────

// Eine Version eines Modells samt Governance-Status (draft/approved/deprecated).
export interface ModelVersionInfo {
  version: string;
  status: string;
}

// Ein Modell der Registry mit seinen Versionen und der aktiven Version (Pointer).
export interface ModelSummary {
  model_id: string;
  versions: ModelVersionInfo[];
  current: string | null;
}

export interface ModelList {
  models: ModelSummary[];
}

// Ein Ein-/Ausgangsport einer Modellversion (aus dem Manifest). Das Manifest ist frei geformtes
// JSON aus dem Trainings-Notebook; die Ansicht liest daraus nur die bekannten Felder heraus.
export interface ModelPort {
  name?: string;
  quantity?: string;
  unit?: string;
  dtype?: string;
  order?: number;
}

// Das Manifest einer Modellversion (GET /models/{name}/{version}). Bewusst tolerant getypt:
// Pflichtfelder (model_id/version) sind gesetzt, der Rest ist optional, weil das Manifest aus dem
// Notebook stammt. Unbekannte Felder bleiben über den Index-Zugriff erhalten.
export interface ModelManifest {
  model_id: string;
  version: string;
  format?: string;
  created_at?: string;
  source_notebook_hash?: string;
  inputs?: ModelPort[];
  outputs?: ModelPort[];
  metrics?: Record<string, number>;
  governance?: {
    status?: string;
    approved_by?: string;
    approved_at?: string;
    note?: string;
  };
  [key: string]: unknown;
}

// ── Datenablage (CSV) + Datenquellen-Zuordnung ────────────────────────────────────────
// Die CSV-Dateien liegen projektspezifisch im NICHT git-versionierten Datenverzeichnis der
// Instanz (BFF_DATA_DIR). NUR die Zuordnung (data-sources.json) ist Teil des git-versionierten
// Projekts. Das Frontend pflegt die Zuordnung grafisch und speichert sie per PUT.

// Ende-der-Datei-Verhalten beim Laufstart: anhalten, loopen oder letzten Wert halten.
export type CsvEndBehavior = 'stop' | 'loop' | 'hold';

// Eine hochgeladene CSV im Projekt-Datenverzeichnis (Name + Größe in Bytes).
export interface DataFileInfo {
  name: string;
  size: number;
}

export interface DataFileList {
  files: DataFileInfo[];
}

// Antwort von GET …/files/{name}/columns: Header, Beispielzeilen (Spalte→Rohwert) und die
// erkannten Kandidaten für die Zeitstempel-Spalte — die Grundlage der grafischen Zuordnung.
export interface DataFileColumns {
  file: string;
  columns: string[];
  sampleRows: Record<string, string>[];
  timestampCandidates: string[];
}

// Eine gespeicherte Datenquelle: welche Datei, Spalte→Tag, Zeitstempel-Spalte, Replay-Optionen.
export interface DataSource {
  id: string;
  label?: string;
  file: string;
  timestampColumn?: string | null;
  // Zuordnung CSV-Spaltenname → Katalog-Eingangs-Tag.
  columnMap: Record<string, string>;
  endBehavior?: CsvEndBehavior;
  loopCount?: number | null;
  loopGapS?: number;
}

export interface DataSources {
  schema_version: number;
  sources: DataSource[];
}

// ── Runs / Monitor ───────────────────────────────────────────────────────────────────

// CSV-Replay-Quelle eines Laufs (dry-run ODER live). Verweist auf eine gespeicherte Zuordnung
// (sourceId) oder gibt Datei + Mapping direkt mit; das Ende-Verhalten wird je Lauf gewählt.
export interface RunCsvSpec {
  sourceId?: string;
  file?: string;
  columnMap?: Record<string, string>;
  timestampColumn?: string | null;
  endBehavior?: CsvEndBehavior;
  loopCount?: number | null;
  loopGapS?: number;
}

export interface RunSourceSpec {
  tag: string;
  kind?: 'const' | 'ramp' | 'sine';
  level?: number;
  start?: number;
  slope?: number;
  amplitude?: number;
  frequency?: number;
  offset?: number;
  phase?: number;
}

// Ein Bibliotheks-Superblock (BORIS-Composite) für die Palette: Referenz, Anzeigename, Familie
// (Gruppierung), Ports (1-indiziert) und Lauffähigkeit (nur in boris2py abgebildete Blocktypen).
// `origin`/`imported` unterscheiden ausgelieferte Default-Superblöcke von per .SBL importierten
// (Issue #106); das Backend liefert beide Felder je Palette-Eintrag.
export interface SuperblockSummary {
  id: string; // Bibliotheks-Referenz (Dateiname der .SBL) — landet in node.params.ref
  name: string;
  family: string;
  inputs: number[];
  outputs: number[];
  runnable: boolean;
  origin?: 'default' | 'imported';
  imported?: boolean;
}

// ── Superblock-Import (.SBL) (Issue #106) ───────────────────────────────────────────────
// Der Nutzer lädt boris2py-.SBL-Dateien hoch; gültige landen instanzweit in der Bibliothek und
// erscheinen danach in der Palette (wie die Defaults). Antwort von POST …/superblocks/import.

// Ein erfolgreich importierter Superblock (deckungsgleich mit den Palette-Feldern).
export interface ImportedSuperblock {
  ref: string;
  name: string;
  family: string;
  inputs: number[];
  outputs: number[];
  runnable: boolean;
}

// Eine übersprungene .SBL samt Grund (ungültig, Dublette, Default-Kollision …).
export interface SkippedSuperblock {
  filename: string;
  reason: string;
}

export interface SuperblockImportResult {
  imported: ImportedSuperblock[];
  skipped: SkippedSuperblock[];
  // Mehrfach-Durchlauf (#271): was NACH diesem Import noch fehlt (inkl. verschachtelter Refs),
  // wenn der Client die referenzierten Refs als `expected` mitgegeben hat. Leer = vollständig.
  stillMissing?: string[];
}

// ── Unbekannte Superblöcke (Massenimport, Issue #271) ───────────────────────────────────
// Welche vom Projekt/Bundle referenzierten Superblöcke der Bibliothek fehlen. `resolved` = nichts
// fehlt. Treiber für den „unbekannter Superblock"-Zustand und die „noch fehlend: …"-Rückmeldung.
export interface SuperblockGapReport {
  referenced: string[];
  missing: string[];
  resolved: boolean;
}

export interface RunCreate {
  mode?: string; // 'dry-run' | 'live'
  cycleMs?: number;
  ticks?: number;
  sources?: RunSourceSpec[];
  // Wenn gesetzt: die Eingänge kommen aus einer CSV (Replay) statt aus dem Sim-Treiber.
  csv?: RunCsvSpec;
  // Für Live-Läufe: der freigegebene Deploy, dessen eingefrorenen Stand der Lauf trägt. Ohne
  // Angabe nutzt das Backend den jüngsten freigegebenen Deploy des Projekts.
  deployId?: string | null;
  // OPC-UA-Endpoint für Live-Läufe (Vorrang vor der Projekt-Einstellung und der Env BFF_OPC_URL);
  // nur im Modus 'live' wirksam.
  opc_url?: string | null;
}

export interface RunInfo {
  runId: string;
  projectId: string;
  state: string; // 'running' | 'stopped'
  mode?: string; // 'dry-run' | 'live'
  deployId?: string | null;
  cycleMs: number;
  // Zeitraffer-Faktor (Abspielgeschwindigkeit gegen die Wall-Clock) und ob er gesperrt ist
  // (Echtzeit 1×, bei Live-OPC aus Sicherheitsgründen). Optional für Rückwärtskompatibilität.
  speed?: number;
  lockSpeed?: boolean;
  tick: number;
}

// Live-Verstellung der Ablaufgeschwindigkeit (POST /runs/{id}/pace): beide Felder optional.
export interface PaceRequest {
  cycleMs?: number;
  speed?: number;
}

// ── Deploys (Live-Freigabe, SAFE-40/41) ───────────────────────────────────────────────
// Ein Live-Lauf (closed loop) setzt einen freigegebenen (approved) Deploy voraus: Stufe 1 friert
// den aktuellen Stand ein (propose), Stufe 2 gibt ihn frei (approve). Ist das Vier-Augen-Prinzip
// des Projekts aus (Default), darf dieselbe Person vorschlagen UND freigeben.

// Vorschlag eines Deploys (POST …/deploys). proposedBy kommt aus der Session — der Body trägt
// höchstens eine optionale Notiz.
export interface DeployCreate {
  note?: string | null;
}

// Freigabe eines Deploys (POST /deploys/{id}/approve). approvedBy kommt aus der Session.
export interface DeployApprove {
  note?: string | null;
}

// Eine im Deploy eingefrorene Modellversion.
export interface DeployModelRef {
  name: string;
  version: string;
}

// Zustand eines Deploy-Vorschlags.
export interface DeployInfo {
  deployId: string;
  projectId: string;
  state: string; // 'pending_approval' | 'approved'
  rev: string | null;
  proposedBy: string;
  proposedAt: string;
  note?: string | null;
  requiresSecondApprover: boolean;
  models: DeployModelRef[];
  approvedBy?: string | null;
  approvedAt?: string | null;
}

export interface DeployList {
  deploys: DeployInfo[];
}

// Läufe eines Projekts samt Zeiger auf den aktiven (noch laufenden) Lauf. Die HMI liest damit
// beim Betreten von Monitor/Dashboard, ob bereits ein Lauf aktiv ist — dann hängt sie sich lesend
// an dessen WS, statt einen zweiten Lauf zu starten. `active` ist null, wenn nichts läuft.
export interface RunList {
  runs: RunInfo[];
  active: RunInfo | null;
}

// WebSocket-Nachrichten des Live-Monitors.
export interface TickValue {
  tag: string;
  value: number;
  unit: string | null;
  quality: string;
  // Nur bei Aktor-Sollwerten (direction === 'output') gesetzt (docs/api.md, FA-RT-21):
  // Regelschleifen-Modus sowie Klemmung/Ratenbegrenzung-Flags des Safety-Blocks. Optional,
  // weil ticks für reine Messsignale diese Felder nicht tragen.
  mode?: 'auto' | 'hand' | 'failsafe';
  clamped?: boolean;
  rateLimited?: boolean;
}

export interface TickSignal {
  node: string;
  port: number;
  value: number;
}

export interface TickMessage {
  op: 'tick';
  runId: string;
  t: number;
  cycleMs: number;
  values: TickValue[];
  signals: TickSignal[];
}

// Ein Safety-Ereignis aus dem Status-Teil des Kanals (docs/api.md, SAFE-42): Klemmung,
// Ratenbegrenzung, QoS-Gating, Watchdog- oder fail-safe-Auslösung — je Tag.
export interface SafetyEvent {
  kind: 'clamp' | 'rate_limit' | 'qos_gate' | 'watchdog' | 'failsafe' | string;
  tag?: string;
  computed?: number;
  applied?: number;
  quality?: string;
  safe_state?: string;
  t?: number;
}

export interface SafetyState {
  watchdog?: 'ok' | 'tripped' | string;
  events?: SafetyEvent[];
}

export interface StatusMessage {
  op: 'status';
  runId: string;
  engineState: 'running' | 'stopped' | string;
  // Optionaler Safety-Zustand (Watchdog + Ereignisliste). Fehlt, solange die Engine nur ticks
  // liefert — die HMI liest ihn defensiv.
  safety?: SafetyState;
  // Aktuelle Ablaufgeschwindigkeit, damit ein frisch verbundener Monitor die Reglerstellung kennt.
  cycleMs?: number;
  speed?: number;
  lockSpeed?: boolean;
}

// Meldet eine live vorgenommene Änderung der Ablaufgeschwindigkeit an alle Monitore des Laufs.
export interface PaceMessage {
  op: 'pace';
  runId: string;
  cycleMs: number;
  speed: number;
  lockSpeed: boolean;
}

export interface ErrorMessage {
  op: 'error';
  runId?: string;
  message: string;
}

export type MonitorMessage = TickMessage | StatusMessage | PaceMessage | ErrorMessage;

// ── Eigener Principal (GET /me) ───────────────────────────────────────────────────────
// Der authentisierte Nutzer, wie ihn das Backend nach außen zeigt (ohne Geheimnisse). Das
// Frontend liest daraus die Rollen und leitet daraus die Rechte ab (siehe hasRight in client.ts).
export interface UserInfo {
  identity: string;
  roles: string[];
  displayName?: string | null;
  profile?: Record<string, unknown>;
  disabled?: boolean;
  hasPassword?: boolean;
  hasToken?: boolean;
  // Lebenszyklus-Status des Kontos (Issue #186..#188): active | pending_email | pending_approval |
  // rejected. Optional, weil ältere/statische Antworten das Feld nicht immer tragen.
  status?: UserStatus;
  email?: string | null;
  emailVerified?: boolean;
  // Ob TOTP-Zweitfaktor aktiv gekoppelt ist (Issue #192). Nicht das Secret selbst.
  mfaEnabled?: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

// Konto-Lebenszyklus (Backend USER_STATUSES). active = anmeldebereit; die übrigen sind
// Zwischen-/Endzustände der Selbstregistrierung (#186/#187).
export type UserStatus = 'active' | 'pending_email' | 'pending_approval' | 'rejected';

// ── Historie / Rollback / Audit (Issue #82) ───────────────────────────────────────────
// Deckungsgleich mit den Pydantic-Modellen des Backends (HistoryResponse, HistoryDiff,
// RevertRequest, AuditResponse). Die Historie ist paginiert (nextBefore = voller Hash der
// nächsten, älteren Seite); der Diff wird pro Zeile lazy nachgeladen; der Revert ist
// nicht-destruktiv (stellt den Zielbaum als NEUEN Commit her) und braucht das Recht „editieren".

// Eine in einem Commit geänderte Objektdatei: Objekttyp, Datei und git-Änderungsart.
export interface HistoryFileChange {
  object: string; // catalog/graph/schematic/data-sources/meta/other
  file: string;
  change: string; // A (add), M (modify), D (delete), R/C …
}

// Ein Commit der Projekt-Historie samt der betroffenen Objekte.
export interface HistoryCommit {
  rev: string; // Kurz-Revision (abbreviated hash)
  fullRev: string; // voller Hash (für before-Paginierung und Diff/Revert)
  author: string;
  ts: string; // Autoren-Zeitstempel (ISO-8601)
  summary: string;
  objects: HistoryFileChange[];
}

// Eine Seite der Historie (neueste zuerst) samt Paginierungs-Zeiger.
export interface HistoryResponse {
  projectId: string;
  commits: HistoryCommit[];
  hasMore: boolean;
  nextBefore: string | null; // voller Hash für die nächste (ältere) Seite oder null
}

// Der Lazy-Diff einer Objektdatei zu einer Revision (Textblock).
export interface HistoryDiff {
  diff: string;
}

// Ergebnis der Audit-Ketten-Verifikation: intakt oder gebrochen (mit Sequenznummer + Grund).
export interface AuditVerify {
  valid: boolean;
  length: number;
  brokenAt: number | null; // Sequenznummer des ersten gebrochenen Glieds oder null
  reason: string | null;
}

// Antwort des Audit-Endpunkts: die Kette (frei geformte Einträge) plus ihr Verifikationsergebnis.
// entries sind bewusst unstrukturiert (dict[str, Any] im Backend) — Anzeige + Export bleiben roh.
export interface AuditResponse {
  projectId: string;
  entries: Record<string, unknown>[];
  verify: AuditVerify;
}

// Einheitlicher Fehlerkörper des Backends (docs/api.md).
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    message_en?: string;
    target?: { kind?: string; id?: string; detail?: string };
  };
}

// ── Benutzer-/Rollenverwaltung (Issue #86, Backend #85) ────────────────────────────────
// Deckungsgleich mit den Pydantic-Modellen (UserInfo/UserList/UserCreate/UserUpdate …).
// Wichtig: Kein Passwort-Hash und kein Token-Klartext in Listen-/Detail-Antworten — nur die
// Login- und Rotate-Antwort tragen das frische Token EINMALIG.

// Die im Backend gültigen Rollen (auth.py VALID_ROLES). Das Frontend hält sie als Union, damit
// Rollen-Auswahl und Anzeige typgeprüft bleiben.
export type Role = 'admin' | 'betreiber' | 'data_scientist' | 'deployer' | 'operator';

export const ROLES: readonly Role[] = [
  'admin',
  'betreiber',
  'data_scientist',
  'deployer',
  'operator',
];

export interface UserList {
  users: UserInfo[];
}

// Anlage-Request eines Nutzers (nur Admin).
export interface UserCreate {
  identity: string;
  password?: string;
  roles?: string[];
  displayName?: string | null;
  profile?: Record<string, unknown>;
}

// Änderung eines Nutzers (nur gesetzte Felder wirken).
export interface UserUpdate {
  roles?: string[];
  displayName?: string | null;
  profile?: Record<string, unknown>;
  disabled?: boolean;
}

// Login-Request/-Antwort (POST /auth/login). Das Token erscheint einmalig im Klartext.
export interface LoginRequest {
  username: string;
  password: string;
}

// Login-Antwort: entweder das Token (ohne MFA) oder eine TOTP-Zweitfaktor-Forderung (Issue #192).
// Bei mfaRequired ist token/user null und mfaTicket gesetzt; das Frontend fragt dann den
// Authenticator-Code ab und ruft POST /auth/login/mfa mit Ticket + Code (oder Recovery-Code).
export interface LoginResponse {
  token?: string | null;
  tokenType: string;
  user?: UserInfo | null;
  mfaRequired?: boolean;
  mfaTicket?: string | null;
}

// Zweiter Login-Schritt (POST /auth/login/mfa): Ticket aus Schritt 1 plus Faktor.
export interface MfaLoginRequest {
  ticket: string;
  code?: string;
  recoveryCode?: string;
}

// Antwort der Token-Rotation (POST /users/{identity}/rotate-token).
export interface TokenResponse {
  token: string;
  tokenType: string;
  user: UserInfo;
}

// ── TOTP-MFA-Kopplung des eigenen Kontos (Issue #192) ──────────────────────────────────
// enroll liefert Secret + otpauth-URI (QR im Frontend); activate bestätigt mit dem ersten Code
// und liefert die Recovery-Codes EINMALIG; disable trennt die Kopplung (mit Passwort).

export interface MfaEnrollResponse {
  secret: string;
  otpauthUri: string;
  issuer: string;
  account: string;
}

export interface MfaActivateResponse {
  recoveryCodes: string[];
  user: UserInfo;
}

export interface MfaStatusResponse {
  mfaEnabled: boolean;
  recoveryCodesRemaining: number;
}

// ── Selbstregistrierung + E-Mail-Verifikation (Issue #186) ─────────────────────────────
// Deckungsgleich mit den Pydantic-Modellen. Kein Token/Passwort in einer Antwort — das
// Verifikations-/Reset-Token erscheint nur in der Mail.

export interface RegisterRequest {
  email: string;
  password: string;
  displayName?: string | null;
  identity?: string | null;
}

export interface RegisterResponse {
  user: UserInfo;
  message: string;
}

export interface VerifyEmailRequest {
  identity: string;
  token: string;
}

export interface VerifyEmailResponse {
  user: UserInfo;
  message: string;
}

export interface ResendVerificationRequest {
  identity: string;
}

export interface ResendVerificationResponse {
  message: string;
}

// ── Passwort vergessen / zurücksetzen (Issue #189) ─────────────────────────────────────
// Beide Endpunkte antworten neutral (immer gleich) — keine E-Mail-/Kennungs-Enumeration.

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface ResetPasswordRequest {
  identity: string;
  token: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  message: string;
}

// ── Admin-Freigabe-Warteschlange (Issue #187) ──────────────────────────────────────────

export interface RegistrationQueue {
  registrations: UserInfo[];
  status: string;
}

export interface ApproveRequest {
  roles?: string[] | null;
}

export interface RejectRequest {
  reason?: string | null;
}

export interface RegistrationDecisionResponse {
  user: UserInfo;
  message: string;
}

// ── Monitoring-Konfiguration (monitoring.json, Issue #81/#135) ─────────────────────────
// Deckungsgleich mit dem Backend-Modell (MonitoringConfig) und monitoring.schema.json. Das
// Dashboard liest daraus die pro-Tag gewählte Anzeigeart (displayTypes) und schreibt Änderungen
// zurück; die übrigen Felder (Dashboards, Alarm-Politik, Übersteuerungen) bleiben dabei erhalten.

// Anzeigeart einer Kachel je Tag im Überwachungs-Dashboard. 'table' ist bewusst NICHT dabei —
// die Tabelle ist eine globale Gesamtansicht, keine Pro-Kachel-Wahl.
export type TileDisplayMode = 'numeric' | 'gauge' | 'trend' | 'bar' | 'lamp' | 'digital';

export interface MonitoringGroup {
  title: string;
  tags: string[];
}

export interface MonitoringDashboard {
  id: string;
  title: string;
  groups: MonitoringGroup[];
}

export interface MonitoringAlarmPolicy {
  requireAck: boolean;
  escalationSec: number;
}

export interface MonitoringThresholdOverride {
  tag: string;
  lolo?: number;
  lo?: number;
  hi?: number;
  hihi?: number;
  reason: string;
}

export interface MonitoringConfig {
  schema_version: number;
  dashboards: MonitoringDashboard[];
  alarmPolicy: MonitoringAlarmPolicy;
  thresholdOverrides: MonitoringThresholdOverride[];
  // Optional/abwärtskompatibel: pro Signal-Tag gewählte Anzeigeart (fehlt ein Tag, gilt der
  // globale Default). Altprojekte ohne dieses Feld liefern es leer.
  displayTypes?: Record<string, TileDisplayMode>;
}

// ── Trainings-Zielparameter (Issue #355) ──────────────────────────────────────────────────
// Welche Optimierungsziele fürs Training gewählt sind und warum. Die Ziel-DEFINITION steht am
// mpc-optimizer-Knoten des Signalgraphen — hier steht nur die Auswahl. `available` liefert das
// Backend abgeleitet mit; es wird beim Schreiben verworfen, damit keine zweite Wahrheitsquelle
// entsteht.

export interface OptimizerGoal {
  node: string;
  output: string;
  tag: string;
  goal: string;
  weight: number;
  scale: number;
  setpoint?: number | null;
  unit?: string | null;
  name?: string | null;
  /** Lesbares Kurzlabel der Größe (#373); fehlt es, steht der Bezeichner allein. */
  label?: string | null;
}

export interface TrainingGoalEntry {
  notebook: string;
  selected: string[];
  note: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface TrainingGoalsConfig {
  schema_version: number;
  projectId: string;
  entries: TrainingGoalEntry[];
  available: OptimizerGoal[];
}

// Ein gestarteter Trainingslauf (Kurzform der Antwort von POST /training/pod/runs).
export interface TrainingRunSummary {
  runId: string;
  status: string;
  notebook: string;
  modelId?: string | null;
  version?: string | null;
}

// ── LLM-Zugang (Issue #359) ───────────────────────────────────────────────────────────────
// Was von einem Anbieter-Zugang nach außen sichtbar ist — NIEMALS der Schlüssel. Nur ob einer
// gesetzt ist, in welcher Form, und seine letzten vier Zeichen zur Wiedererkennung.

export interface LlmProviderView {
  keySet: boolean;
  keyKind?: 'bao' | 'enc' | null;
  keyHint?: string | null;
  model?: string | null;
}

export interface LlmConfigView {
  schema_version: number;
  anthropic: LlmProviderView;
  openai: LlmProviderView;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

// Was für einen Anbieter tatsächlich greift — und aus welcher Ebene (projekt | nutzer | server).
export interface LlmResolution {
  provider: string;
  model: string;
  keySet: boolean;
  keyFrom?: string | null;
  modelFrom?: string | null;
}

export interface ProjectLlmConfig {
  projectId: string;
  project: LlmConfigView;
  effective: LlmResolution[];
  models: Record<string, string[]>;
  encryptionAvailable: boolean;
}

export interface LlmConfigUpdate {
  anthropicApiKey?: string;
  anthropicModel?: string;
  openaiApiKey?: string;
  openaiModel?: string;
  clearAnthropicKey?: boolean;
  clearOpenaiKey?: boolean;
}

// ── Notebook-Builder (Issue #363) ─────────────────────────────────────────────────────────

export interface NotebookGenerateResult {
  projectId: string;
  notebook: string;
  sha256: string;
  provider: string;
  model: string;
  modelFrom?: string | null;
  keyFrom?: string | null;
  codePreview: string;
}

export interface NotebookRunResult {
  projectId: string;
  ok: boolean;
  returncode: number;
  output: string;
  artifactFound: boolean;
  modelId?: string | null;
  version?: string | null;
  graphUpdated: boolean;
  graphNote?: string | null;
}
